import { Link } from '@tanstack/react-router';
import { IconExternalLink, IconGitCompare } from '@tabler/icons-react';
import { AQF_LEVEL_LABELS, type AqfLevel, type CourseDetail } from '@admitly/shared';
import { centsToAud, formatIntakes } from '../../lib/format.js';
import { cn } from '../../lib/cn.js';

type Props = {
  courses: CourseDetail[];
};

type Row = {
  label: string;
  render: (c: CourseDetail) => React.ReactNode;
};

const dash = (v: React.ReactNode) => v || <span className="text-muted-soft">—</span>;

const ROWS: Row[] = [
  { label: 'Provider', render: (c) => c.providerName },
  { label: 'CRICOS', render: (c) => dash(c.cricosCode) },
  { label: 'Annual tuition', render: (c) => centsToAud(c.annualTuitionCents) },
  { label: 'Total program', render: (c) => centsToAud(c.totalProgramCents) },
  { label: 'OSHC (annual)', render: (c) => centsToAud(c.oshcAnnualCents) },
  { label: 'Application fee', render: (c) => centsToAud(c.applicationFeeCents) },
  { label: 'Intakes', render: (c) => dash(c.intakeMonths.length ? formatIntakes(c.intakeMonths) : null) },
  { label: 'Location', render: (c) => dash(c.campusLocations.length ? c.campusLocations.join(', ') : null) },
  { label: 'State', render: (c) => dash(c.providerPrimaryState) },
  { label: 'AQF level', render: (c) => dash(c.aqfLevel ? AQF_LEVEL_LABELS[c.aqfLevel as AqfLevel] : null) },
  {
    label: 'English (IELTS)',
    render: (c) => dash(c.ieltsOverall ? `${c.ieltsOverall}${c.ieltsRequirementText ? ` · ${c.ieltsRequirementText}` : ''}` : null),
  },
  { label: 'PR pathway', render: (c) => (c.prRelevant ? 'Yes' : 'No') },
];

export function CompareTable({ courses }: Props) {
  if (courses.length === 0) return null;

  return (
    <section className="surface p-0 overflow-x-auto">
      <div className="flex items-center gap-2 px-5 pt-5 pb-3">
        <IconGitCompare size={18} className="text-gold" />
        <h2 className="font-serif text-lg text-ink tracking-tightish">
          Comparing {courses.length} course{courses.length === 1 ? '' : 's'}
        </h2>
      </div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-t border-line">
            <th className="sticky left-0 z-20 bg-white text-left align-bottom p-3 w-32 sm:w-40 text-[10px] uppercase tracking-[0.12em] text-muted font-semibold">
              Field
            </th>
            {courses.map((c) => (
              <th key={c.id} className="text-left align-bottom p-3 min-w-[12rem] border-l border-line">
                <div className="font-serif text-base text-ink leading-snug">{c.name}</div>
                <div className="text-xs text-muted mt-0.5">{c.providerShortName ?? c.providerName}</div>
                <Link
                  to="/app/courses/$category/$subCategory/$courseId"
                  params={{ category: c.category, subCategory: c.subCategory, courseId: c.id }}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
                >
                  <IconExternalLink size={13} /> Open
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row, i) => (
            <tr key={row.label} className={i % 2 === 0 ? 'bg-cream-deep/20' : ''}>
              {/* Sticky label column: opaque bg so it stays readable while the
                  course columns scroll under it on mobile. */}
              <td
                className={cn(
                  'sticky left-0 z-10 p-3 align-top text-[10px] uppercase tracking-[0.12em] text-muted font-semibold',
                  i % 2 === 0 ? 'bg-[#FBF7EE]' : 'bg-white',
                )}
              >
                {row.label}
              </td>
              {courses.map((c) => (
                <td key={c.id} className="p-3 align-top text-ink-soft border-l border-line">
                  {row.render(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
